import { useNavigate } from "react-router-dom";
import {
  Database,
  Search,
  Bot,
  LayoutDashboard,
  MessageCircle,
  LayoutTemplate,
  ExternalLink,
  Lightbulb,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

interface HowItWorksStep {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  details: string[];
  examples?: string[];
  explorePath: string;
  exploreLabel: string;
}

const howItWorksSteps: HowItWorksStep[] = [
  {
    id: "connect",
    icon: Database,
    title: "Connect your data sources",
    description:
      "Connect databases, social channels, and enterprise software to your private DataBull cluster.",
    details: [
      "Supports PostgreSQL, MySQL, MongoDB, BigQuery, Snowflake, and 50+ more databases.",
      "Connect social channels like Facebook, Instagram, and Twitter for social analytics.",
      "Integrate enterprise software such as Salesforce, HubSpot, Shopify, and more.",
      "All data stays within your private cluster — secure and encrypted.",
    ],
    explorePath: "/data-connectors",
    exploreLabel: "Connect Data Sources",
  },
  {
    id: "research",
    icon: Search,
    title: "Research within your data to get business insights",
    description:
      "Ask natural language questions about your data and get instant, actionable insights.",
    details: [
      "Use plain English to query across all your connected data sources.",
      "Get visualizations, charts, and tables generated automatically.",
      "Deep-dive into trends, anomalies, and patterns without writing SQL.",
    ],
    examples: [
      "Why did my sales drop yesterday?",
      "Give me details about users who have provided 1-star reviews",
      "What are the top 10 products by revenue this month?",
    ],
    explorePath: "/research",
    exploreLabel: "Start Researching",
  },
  {
    id: "workflows",
    icon: Bot,
    title: "Build agentic workflows to automate day-to-day operations",
    description:
      "Create automated workflows that run on schedule or trigger based on events to keep you informed.",
    details: [
      "Schedule recurring reports and alerts to be delivered automatically.",
      "Set up event-driven triggers — get notified when metrics cross thresholds.",
      "Chain multiple actions together for complex automation pipelines.",
    ],
    examples: [
      "Send me an email about yesterday's sales data every day at 7 AM",
      "Alert me on Slack when daily revenue drops below $10,000",
      "Generate a weekly performance summary every Monday morning",
    ],
    explorePath: "/aiagents",
    exploreLabel: "Build Workflows",
  },
  {
    id: "dashboards",
    icon: LayoutDashboard,
    title: "Build standard dashboards & reports for your business",
    description:
      "Create beautiful, interactive dashboards and scheduled reports tailored to your business needs.",
    details: [
      "Drag-and-drop dashboard builder with charts, tables, KPIs, and more.",
      "Share dashboards with your team or embed them in your own applications.",
      "Schedule reports to be generated and sent automatically on a recurring basis.",
    ],
    explorePath: "/dashboards",
    exploreLabel: "View Dashboards",
  },
  {
    id: "messaging",
    icon: MessageCircle,
    title: "Connect WhatsApp and other messaging apps",
    description:
      "Do research directly within your favourite messaging channel — no need to open a browser.",
    details: [
      "Connect WhatsApp, Telegram, Slack, Google Chat, and Microsoft Teams.",
      "Ask questions about your data directly in the chat — get instant answers.",
      "Share insights with your team right where they already communicate.",
    ],
    explorePath: "/external-interface",
    exploreLabel: "Connect Messaging Apps",
  },
  {
    id: "templates",
    icon: LayoutTemplate,
    title: "Choose from thousands of predefined templates",
    description:
      "Get started instantly with ready-made templates based on your industry and use case.",
    details: [
      "Browse templates for e-commerce, SaaS, marketing, finance, and more.",
      "Each template comes with pre-built dashboards, queries, and workflows.",
      "Customize any template to fit your specific business requirements.",
    ],
    explorePath: "/advanced-features",
    exploreLabel: "Browse Templates",
  },
];

interface HowItWorksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HowItWorksDialog = ({ open, onOpenChange }: HowItWorksDialogProps) => {
  const navigate = useNavigate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
            How does it work?
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Get started with DataBull in a few simple steps
          </p>
        </DialogHeader>

        <Accordion type="single" collapsible className="w-full">
          {howItWorksSteps.map((step, idx) => (
            <AccordionItem key={step.id} value={step.id}>
              <AccordionTrigger className="gap-3 text-left hover:no-underline [&>svg]:shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <step.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Step {idx + 1}</span>
                    <p className="text-sm font-medium leading-snug">{step.title}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="ml-11 space-y-4">
                  <p className="text-muted-foreground">{step.description}</p>

                  <ul className="space-y-1.5">
                    {step.details.map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        {d}
                      </li>
                    ))}
                  </ul>

                  {step.examples && (
                    <div className="rounded-md border border-border bg-muted/50 p-3">
                      <p className="mb-2 text-xs font-medium text-muted-foreground">Examples</p>
                      <ul className="space-y-1">
                        {step.examples.map((ex, i) => (
                          <li key={i} className="text-sm italic text-foreground">
                            "{ex}"
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(step.explorePath);
                    }}
                    className="gap-1.5"
                  >
                    {step.exploreLabel}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};

export default HowItWorksDialog;
